import React, { useState, useEffect } from 'react';
import { Button, Modal, Input } from '@/components/ui';
import type { ConstructionContract } from '../types/construction-contracts.types';
import { useConstructionContracts } from '../hooks/useConstructionContracts';
import { useToast } from '@/components/ui/Toast';
import { toInputDate } from '@/utils/format';

interface ContractLegalFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: ConstructionContract;
  companyId: string;
}

export function ContractLegalFormModal({ isOpen, onClose, contract, companyId }: ContractLegalFormModalProps) {
  const { toast } = useToast();
  const { updateContract, isUpdating } = useConstructionContracts(companyId);

  const [formData, setFormData] = useState<Partial<ConstructionContract>>({});
  const [advanceRate, setAdvanceRate] = useState<number>(0);
  const [isManualAmount, setIsManualAmount] = useState(false);

  useEffect(() => {
    if (isOpen && contract) {
      const initialAmount = contract.advance_payment_amount || 0;
      const initialValue = contract.contract_value || 0;
      const initialRate = initialValue > 0 ? (initialAmount / initialValue) * 100 : 0;

      setFormData({
        client_name: contract.client_name || '',
        contractor_name: contract.contractor_name || '',
        signed_date: contract.signed_date || '',
        warranty_months: contract.warranty_months || 0,
        scope_summary: contract.scope_summary || '',
        advance_payment_amount: initialAmount,
        payment_terms: contract.payment_terms || '',
        contract_value: initialValue,
        retention_rate: contract.retention_rate || 0,
      });
      setAdvanceRate(Number(initialRate.toFixed(2)));
      setIsManualAmount(false);
    }
  }, [isOpen, contract]);

  const handleAdvanceRateChange = (rate: number) => {
    const amount = (formData.contract_value || 0) * (rate / 100);
    setFormData({ ...formData, advance_payment_amount: Math.round(amount) });
    setAdvanceRate(rate);
  };

  const handleAdvanceAmountChange = (amount: number) => {
    setFormData({ ...formData, advance_payment_amount: amount });
    const rate = (formData.contract_value || 0) > 0 ? (amount / formData.contract_value!) * 100 : 0;
    setAdvanceRate(Number(rate.toFixed(2)));
    setIsManualAmount(amount > 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateContract({ id: contract.id, updates: formData });
      onClose();
      toast.success('Cập nhật thông tin pháp lý thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cập nhật Thông tin Pháp lý & Tài chính" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Chủ đầu tư (Client)"
            value={formData.client_name || ''}
            onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
            placeholder="Tên đơn vị chủ đầu tư"
          />
          <Input
            label="Nhà thầu (Contractor)"
            value={formData.contractor_name || ''}
            onChange={(e) => setFormData({ ...formData, contractor_name: e.target.value })}
            placeholder="Tên đơn vị thi công"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Ngày ký kết"
            type="date"
            value={toInputDate(formData.signed_date)}
            onChange={(e) => setFormData({ ...formData, signed_date: e.target.value })}
          />
          <Input
            label="Thời gian bảo hành (tháng)"
            type="number"
            value={formData.warranty_months || ''}
            onChange={(e) => setFormData({ ...formData, warranty_months: Number(e.target.value) })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Giá trị hợp đồng (VNĐ)"
            type="number"
            value={formData.contract_value || ''}
            onChange={(e) => {
              const newVal = Number(e.target.value);
              setFormData({ ...formData, contract_value: newVal });
              // Recalculate amount if using rate
              if (!isManualAmount) {
                setFormData(prev => ({ ...prev, advance_payment_amount: Math.round(newVal * (advanceRate / 100)) }));
              }
            }}
          />
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Input
                label="Số tiền Tạm ứng (VNĐ)"
                type="number"
                value={formData.advance_payment_amount || ''}
                onChange={(e) => handleAdvanceAmountChange(Number(e.target.value))}
              />
            </div>
            <div>
              <Input
                label="Tỉ lệ %"
                type="number"
                value={advanceRate}
                onChange={(e) => handleAdvanceRateChange(Number(e.target.value))}
                disabled={isManualAmount}
                className={isManualAmount ? 'bg-slate-50 opacity-50 cursor-not-allowed' : ''}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Tỉ lệ giữ lại (%)"
            type="number"
            value={formData.retention_rate || ''}
            onChange={(e) => setFormData({ ...formData, retention_rate: Number(e.target.value) })}
          />
        </div>

        <div className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-700">Điều khoản thanh toán</label>
            <textarea
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 min-h-[100px]"
              value={formData.payment_terms || ''}
              onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
              placeholder="Nhập chi tiết các đợt thanh toán..."
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-700">Tóm tắt phạm vi thi công</label>
            <textarea
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 min-h-[100px]"
              value={formData.scope_summary || ''}
              onChange={(e) => setFormData({ ...formData, scope_summary: e.target.value })}
              placeholder="Mô tả tóm tắt các hạng mục thi công chính..."
            />
          </div>
        </div>

        <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
          <Button variant="outline" type="button" onClick={onClose} disabled={isUpdating}>
            Hủy
          </Button>
          <Button variant="primary" type="submit" isLoading={isUpdating}>
            Lưu thay đổi
          </Button>
        </div>
      </form>
    </Modal>
  );
}
